<script>
import { getInitials } from '@utils/Format'
import TranscriptStateFeedback from '@modules/crm/components/transcript-state-feedback/transcript-state-feedback.vue'

export default {
  name: 'TranscriptParticipants',
  components: { TranscriptStateFeedback },
  props: {
    participants: { type: Array, default: () => [] },
    editable: { type: Boolean, default: false },
  },
  computed: {
    normalized() {
      // O backend pode devolver o participante como string (apenas nome) OU
      // como objeto { name, email? }. Normaliza p/ objeto.
      return this.participants.map((p) => {
        if (typeof p === 'string') {
          return { name: p, email: null }
        }
        // Backend/extensão serializa em PT-BR (`nome`); aceitamos `name` (EN)
        // como fallback p/ não exibir "Sem nome" quando o dado existe.
        return {
          name: (p && (p.nome || p.name)) || 'Sem nome',
          email: (p && (p.email || p.e_mail)) || null,
        }
      })
    },
    countLabel() {
      const n = this.normalized.length
      return n + ' participante' + (n === 1 ? '' : 's')
    },
  },
  methods: {
    getInitials,
    abrirEdicao() {
      this.$emit('edit')
    },
  },
}
</script>

<template>
  <div>
    <!-- Card com cabeçalho (contagem à esquerda, editar à direita) e os
         participantes em blocos cinza dentro. -->
    <div v-if="normalized.length" class="transcript-participants__card rounded-sm pa-4">
      <div class="d-flex align-center justify-space-between mb-4">
        <span class="table-new-ux-v1-title">{{ countLabel }}</span>
        <v-tooltip v-if="editable" top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon x-small v-bind="attrs" v-on="on" @click="abrirEdicao">
              <icon-svg name="edit" size="16" color="var(--muted)" />
            </v-btn>
          </template>
          <span>Editar participantes</span>
        </v-tooltip>
      </div>

      <div class="d-flex flex-column az-gap-small">
        <div
          v-for="(p, i) in normalized"
          :key="i"
          class="transcript-participants__item"
        >
          <v-avatar size="32" class="transcript-participants__avatar mr-3 text-color-100">
            {{ getInitials(p.name) }}
          </v-avatar>
          <div class="d-flex flex-column flex-grow-1" style="min-width: 0">
            <span
              class="az-text-primary az-size-14 font-semibold text-truncate"
            >
              {{ p.name }}
            </span>
            <span class="az-text-small text-truncate">{{
              p.email || '-'
            }}</span>
          </div>
          <v-tooltip v-if="editable" top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="ml-2 flex-shrink-0"
                v-bind="attrs"
                v-on="on"
                @click="$emit('remove', { index: i, participant: p })"
              >
                <icon-svg name="trash" size="16" color="var(--muted)" />
              </v-btn>
            </template>
            <span>Remover</span>
          </v-tooltip>
        </div>

        <!-- Bloco pontilhado para adicionar (abre o modal já com campo novo) -->
        <div
          v-if="editable"
          class="transcript-participants__add az-text-small"
          @click="$emit('add')"
        >
          + Adicionar participante
        </div>
      </div>
    </div>

    <!-- Vazio: o botão de adicionar fica dentro do próprio feedback -->
    <TranscriptStateFeedback
      v-else
      state="empty"
      icon="mdi-account-group-outline"
      message="Nenhum participante registrado para esta reunião."
    >
      <template v-if="editable" v-slot:button>
        <BaseButton sm fitContent primary @click="abrirEdicao">
          Adicionar participante
        </BaseButton>
      </template>
    </TranscriptStateFeedback>
  </div>
</template>

<style lang="sass" scoped>
.transcript-participants
  // Card externo: fundo cinza-claro + borda (mesmo padrão dos demais blocos).
  &__card
    background: #F8F9FA
    border: 1px solid #F0F1F3

  // Bloco branco por participante (contrasta com o card cinza, igual aos itens
  // das outras abas).
  &__item
    display: flex
    align-items: center
    background: var(--white)
    border: 1px solid #F0F1F3
    border-radius: 8px
    padding: 8px 12px

  // Avatar no padrão do gerenciador de tarefas (círculo primary, iniciais
  // brancas, borda branca). Fonte branca é intrínseca ao avatar.
  &__avatar
    flex-shrink: 0
    background-color: var(--primary)
    font-family: 'Archivo', sans-serif
    font-size: 11px
    font-weight: 600
    border: 2px solid var(--white)

  // Bloco pontilhado "adicionar participante"
  &__add
    display: flex
    align-items: center
    justify-content: center
    border: 1px dashed var(--gray-3, #C4C4C4)
    border-radius: 8px
    padding: 10px 12px
    cursor: pointer
    transition: background 0.2s

    &:hover
      background: #F5F6FA
</style>
